import React, { FC, MouseEventHandler, RefObject, useContext, useEffect, useRef } from "react";
import cx from "classnames";
import Flyout from "@app/shared/Flyouts/Flyout";
import { FlyoutContent } from "@app/shared/Flyouts/flyoutContent";
import FlyoutContextProvider, { FlyoutContext } from "@app/shared/Flyouts/FlyoutContext";
import { ModalContent } from "@app/shared/Modals";
import { QESButton } from "@app/shared/qes-standards";

interface FlyoutLinkProps {
  /**
   * The element shown inside the flyout link button. This has no relation to the content of the flyout.
   */
  children: React.ReactNode;
  /**
   * Optional classnames to apply to the flyout link button. "qes-button-secondary" or "qes-button-primary" are recommended.
   */
  classNames?: string;
  /**
   * The content of the flyout that will be opened upon clicking the button.
   * This consists of a body (JSX.Element), a title (string), optional subTitle (string) to be shown below title and optional buttons to show along the top of the flyout. This last prop is unused in QES as of July 2023.
   */
  content: FlyoutContent;
  /**
   * Disables the flyoutlink button
   */
  disabled?: boolean;
  /**
   * By default, flyouts will monitor their internal DOMs for changes and refocus on the first focusable body element.
   * This was intended to autofocus on newly loaded content instead of focusing on the close button,
   * but in some cases (like with a text filter input) we expect the DOM to change and don't want to move the focus.
   */
  ignoreDomUpdatesInFlyout?: boolean;
  /**
   * Allows for a flyout to be opened by default, or programmatically without a user clicking the button
   */
  isVisible?: boolean;
  /**
   * Optional content for a modal that would appear when closing the flyout. This only happens if warnOnClose is also true.
   * ModalContent consists of a body (JSX.Element), cancelButtonText (string), confirmButtonText (string), confirmButtonType (optional, QESButtonTypes), and title (string).
   */
  modalContent?: Partial<ModalContent>;
  /**
   * Optional function to trigger when the flyout closes
   */
  onClose?: () => void;
  /**
   * Optional function to trigger when the flyout opens
   */
  onOpen?: MouseEventHandler<HTMLButtonElement>;
  /**
   * Optional id to give the flyout
   */
  panelId?: string;
  /**
   * Optional size indicator for the flyout. These number are a percentage of the screen width.
   * The goal is to have a flyout's width be close to the width of its contents.
   * This defauls to 60.
   */
  size?: "20" | "40" | "60" | "80";
  /**
   * Required unique, static value used by QA Automation for testing. This goes on the flyout link button, and is not passed to the flyout.
   * The format of the qesTestId was retained when building this component and replacing the old SidePanelLink for backwards compatibility.
   */
  testId: string;
  /**
   * A ref used to override the default flyoutButton in this component.
   * This is used to point the flyout somewhere else when it closes.
   * For example, if this button will no longer exist or be visible when the flyout closes.
   */
  triggerElementOverride?: RefObject<HTMLButtonElement>;
  /**
   * Optional boolean to trigger a modal when closing the flyout. The modal will close the flyout on confirmation, or return to the flyout on cancel.
   * Strongly recommended this is used in conjunction with modalContent, unless you want an empty modal.
   */
  warnOnClose?: boolean;
}

const WrappedFlyoutLink: FC<FlyoutLinkProps> = (props: FlyoutLinkProps) => {
  const {
    handleOpen,
    hasOpened,
    isFlyoutVisible,
    setIsFlyoutVisible,
  } = useContext(FlyoutContext);
  const flyoutButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isFlyoutVisible && hasOpened) {
      if (!!props.onClose) {
        props.onClose();
      }
    }
  }, [isFlyoutVisible]);

  useEffect(() => {
    if (props.isVisible !== undefined) {
      setIsFlyoutVisible(props.isVisible);
    }
  }, [props.isVisible]);

  const wrappedHandleOpen: MouseEventHandler<HTMLButtonElement> = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (props.onOpen) {
      props.onOpen(e);
    }
    handleOpen();
  }

  return (
    <>
      <QESButton
        className={cx({
          "side-panel-link": true,
          [props.classNames]: !!props.classNames,
        })}
        disabled={props.disabled}
        onClick={wrappedHandleOpen}
        qesTestId={`side-panel-link__${props.testId}-side-panel-link-button`}
        qesType="none"
        qesRef={flyoutButton}
      >
        {props.children}
      </QESButton>
      <Flyout
        content={props.content}
        modalContent={props.modalContent}
        panelId={props.panelId}
        size={props.size}
        triggerElement={props.triggerElementOverride || flyoutButton}
      />
    </>
  );
}

/**
 * An unstyled button component that, on click, opens a flyout defined in the props.<br />
 * Flyouts are preferred over Modals when there is more user interaction than a simple "yes/no" prompt.
 */
const FlyoutLink: FC<FlyoutLinkProps> = (props: FlyoutLinkProps) => {
  return (
    <FlyoutContextProvider title={props.content.title} warnOnClose={props.warnOnClose} ignoreDomUpdates={props.ignoreDomUpdatesInFlyout}>
      <WrappedFlyoutLink {...props} />
    </FlyoutContextProvider>
  );
}

export default FlyoutLink;